import { computed, inject, Injectable, signal } from '@angular/core';
import { produce } from 'immer';
import { deepMerge, GuidelineService } from 'shared/domain';

import { AnchoredComment, CommentService } from '@treetop/shared/comments';
import { FlowchartElementType, FlowchartNodeDto, FlowchartNodeType, GuidelineOrigin, FlowchartWidget } from '@treetop/shared/model';

export enum FlowchartSidebarType {
  Start,
  Action,
  Decision,
  Link,
  TextBlock,
  Container,
}

@Injectable()
export class FlowchartSidebarWrapperService {
  private guidelineService = inject(GuidelineService);
  private commentsService = inject(CommentService);

  private selectedWidgetId = signal<string | undefined>(undefined);
  private selectedElementId = signal<string | undefined>(undefined);

  public vm = computed(() => ({
    element: this.element(),
    sidebarType: this.sidebarType(),
    flowchart: this.elements(),
    showAddCommentButton: this.showAddCommentButton(),
  }));

  private flowchart = computed(() => {
    return this.guidelineService.selectElement<FlowchartWidget>(this.selectedWidgetId())();
  });

  private showAddCommentButton = computed(() => {
    if (!this.commentsService.initialized() || this.commentsService.readonly()) {
      return false;
    }

    const sidebarType = this.sidebarType();
    if (sidebarType === undefined || sidebarType === null) {
      return false;
    }

    const element = this.element();
    if (!element) {
      return false;
    }

    const comment = this.commentsService
      .comments()
      .find(
        (comment) =>
          comment.anchor.type === 'flowchart-element' &&
          comment.anchor.widgetId === this.flowchart()?.uniqueId &&
          comment.anchor.elementId === (this.element()?.uniqueId ?? this.element()?.id),
      );

    if (comment) {
      return false;
    }

    if (element.elementType === FlowchartElementType.FlowchartStartNode) {
      return !!element.guidelineAction || !!element.guidelineCriteria;
    }

    if (sidebarType === FlowchartSidebarType.Action) {
      return (
        !!element.guidelineAction ||
        element.elementType === FlowchartElementType.FlowchartContainer ||
        element.elementType === FlowchartElementType.FlowchartSubflow
      );
    }

    if (sidebarType === FlowchartSidebarType.Decision) {
      return !!element.guidelineCriteria || element.guidelineObservation;
    }

    if (sidebarType === FlowchartSidebarType.TextBlock || sidebarType === FlowchartSidebarType.Link) {
      return true;
    }
  });

  private elements = computed(() => {
    const flowchartWidget = this.flowchart();
    if (flowchartWidget.localChanges?.elements) {
      return flowchartWidget.localChanges.elements;
    }

    return flowchartWidget.elements ?? [];
  });

  private element = computed<FlowchartNodeType | undefined>(() => {
    const elements = this.elements();

    const selectedElementId = this.selectedElementId();
    if (!selectedElementId) return undefined;

    const findComponent = (elements: FlowchartNodeType[], uniqueId: string): FlowchartNodeType | undefined => {
      for (const element of elements) {
        if (element.uniqueId === uniqueId) return element;

        if (element.children) {
          const found = findComponent(element.children, uniqueId);
          if (found) return found;
        }

        if (element.outgoingLinks) {
          const foundInLinks = element.outgoingLinks.find((link) => link.id === uniqueId);
          if (foundInLinks) return foundInLinks;
        }
      }
      return undefined;
    };

    return findComponent(elements, selectedElementId);
  });

  private flatElements = computed(() => {
    const elements = this.elements();

    const flatten = (elements: FlowchartNodeType[]): FlowchartNodeType[] => {
      return elements.reduce<FlowchartNodeType[]>((acc, element) => {
        acc.push(element);
        if (element.children) {
          acc.push(...flatten(element.children));
        }
        return acc;
      }, []);
    };

    return flatten(elements);
  });

  private sidebarType = computed(() => {
    const flowchartElement = this.element();

    if (!flowchartElement) {
      return undefined;
    }

    if (flowchartElement.elementType === FlowchartElementType.FlowchartStartNode) {
      return FlowchartSidebarType.Start;
    }

    if (
      flowchartElement.elementType === FlowchartElementType.FlowchartAction ||
      flowchartElement.elementType === FlowchartElementType.FlowchartSubflow
    ) {
      return FlowchartSidebarType.Action;
    }

    if (flowchartElement.elementType === FlowchartElementType.FlowchartContainer) {
      return FlowchartSidebarType.Container;
    }

    if (flowchartElement.elementType === FlowchartElementType.FlowchartDecisionNode) {
      return FlowchartSidebarType.Decision;
    }

    if (flowchartElement.elementType === FlowchartElementType.FlowchartLink) {
      return FlowchartSidebarType.Link;
    }

    if (flowchartElement.elementType === FlowchartElementType.FlowchartTextBlock) {
      return FlowchartSidebarType.TextBlock;
    }
  });

  public setSelectedWidgetId(widgetId: string): void {
    this.selectedWidgetId.set(widgetId);
  }

  public setSelectedElementId(elementId: string): void {
    this.selectedElementId.set(elementId);
  }

  public getFlowchartElement(elementId: string): FlowchartNodeType | undefined {
    return this.flatElements().find((el) => el.uniqueId === elementId);
  }

  public handleFlowchartElementDataChanged(data: Partial<FlowchartNodeType>) {
    const elementId = this.element().uniqueId ?? this.element().id;

    const flowchart = this.guidelineService.getWidget<FlowchartWidget>(this.flowchart().uniqueId);
    if (!flowchart) return;

    const elementsToUpdate = flowchart.localChanges?.elements ?? flowchart.elements ?? [];

    const updateElement = (elements: FlowchartNodeDto[] = []): FlowchartNodeDto[] => {
      return elements.map((element) =>
        produce(element, (draft) => {
          if (element.id === elementId || element.uniqueId === elementId) {
            Object.assign(draft, deepMerge(draft, data));
          } else if (element.children?.length) {
            draft.children = updateElement(element.children);
          }

          if (element.outgoingLinks?.length > 0) {
            draft.outgoingLinks = element.outgoingLinks.map((link) => {
              if (link.id === elementId) {
                return data;
              } else {
                return link;
              }
            });
          }
        }),
      );
    };

    const updatedElements = updateElement(elementsToUpdate);

    if (this.guidelineService.guidelineData().guidelineOrigin === GuidelineOrigin.IMPORTED) {
      this.guidelineService.updateWidgetLocal<FlowchartWidget>(this.flowchart().uniqueId, { elements: updatedElements });
    } else {
      const newWidget = produce(this.flowchart(), (draft) => {
        draft.elements = updatedElements;
      });

      this.guidelineService.updateWidget(this.flowchart().uniqueId, newWidget);
    }
  }

  public addCommentToElement() {
    const comment: AnchoredComment = {
      id: crypto.randomUUID(),
      text: '',
      type: this.commentsService.newCommentType(),
      anchor: {
        type: 'flowchart-element',
        widgetId: this.flowchart().uniqueId,
        elementId: this.element().uniqueId ?? this.element().id,
      },
    };

    this.commentsService.addComment(comment);
  }
}
